import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Typography,
  DatePicker,
  TimePicker,
  Button,
  message,
  Tag,
  Row,
  Col,
} from "antd";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import PackageTable from "../../../Receptionist/Billing/PackageTable";
import PatientPackageDetails from "./PatientPackageDetails";
import { getPackages } from "../../../../services/service.services";
import { assignPackageApi } from "../../../../services/receptionist.services";
import useQuery from "../../../../hooks/useQuery";
import dayjs from "dayjs";
import { getCookie } from "../../../../helpers/localStorage";
import { FacilityID } from "../../../../constants/defaultKeys";
import { UserContext } from "../../../../context/UserContext";
import styles from "./PackagesIp.module.css";

const priceTagList = [
  { name: "₹", id: false },
  { name: "%", id: true },
];

const { Option } = Select;

const AssignPackages = ({ patientData }) => {
  const { userDetails } = useContext(UserContext);
  const params = useQuery();
  const [form] = Form.useForm();
  const [packages, setPackages] = useState([]);
  const [selectPackageId, setSelectPackageId] = useState(null);
  const [viewDetails, setViewDetails] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );
  const admission = patientData?.admission?.is_active;
  const facilityId = Number(getCookie(FacilityID)) || userDetails?.currFacility;
  const orgPrimaryColor =
    userDetails?.activeOrg?.color || "var(--ant-primary-color, #6941C6)";
  const refreshPackage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    getPackages(`facility_id=${facilityId}&package_type=IP`)
      .then((res) => {
        setPackages(res?.data?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [facilityId]);

  const resetAssignForm = () => {
    form.resetFields();
    form.setFieldsValue({
      start_date: dayjs(),
      mobile_pkg_date: dayjs(),
      mobile_pkg_time: dayjs(),
      discount: "",
      package: undefined,
    });
    setDiscount("");
    setDiscountType(false);
    setSelectPackageId(null);
    setViewDetails(false);
    setTotalPrice(0);
  };

  const onFinish = (values) => {
    let discountInRupees = discount;

    if (discountType === true) {
      discountInRupees = (discount / 100) * totalPrice;
    }

    let startMoment;
    if (isMobile) {
      const d = values?.mobile_pkg_date;
      const t = values?.mobile_pkg_time;
      if (!d || !t) {
        message.error("Please select date and time");
        return;
      }
      startMoment = dayjs(d)
        .hour(t.hour())
        .minute(t.minute())
        .second(0)
        .millisecond(0);
    } else {
      startMoment = dayjs(values?.start_date);
    }

    const formData = {
      start_date: startMoment.toISOString(),
      package: values?.package,
      discount: discountInRupees || 0,
    };

    // console.log(formData);

    assignPackageApi(params?.patient, formData)
      .then((res) => {
        message.success(res?.data?.message);
        resetAssignForm();
        refreshPackage();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message);
      });
  };

  const onDiscountTypeChange = (value) => {
    setDiscountType(value);
    setDiscount("");
    form.setFieldValue("discount", "");
    form.setFields([{ name: "discount", errors: [] }]);
  };

  return (
    <>
      <Card
        style={{ "--org-primary": orgPrimaryColor }}
        title={
          isMobile ? (
            <div className={styles.assignPackageTitleRow}>
              <Typography.Text strong className={styles.assignPackageTitle}>
                Assign Package
              </Typography.Text>
              <Button
                type="primary"
                className={`${btnStyles.mdBtn} ${styles.assignPackageSubmitTop}`}
                disabled={!admission}
                onClick={() => form.submit()}
              >
                Submit
              </Button>
            </div>
          ) : (
            "Assign Package"
          )
        }
        className={`care-item-card ${styles.assignPackageCard} ${
          isMobile ? styles.assignPackageCardMobile : ""
        }`}
      >
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          className={styles.assignPackageForm}
          style={{ "--org-primary": orgPrimaryColor }}
          initialValues={{
            start_date: dayjs(),
            mobile_pkg_date: dayjs(),
            mobile_pkg_time: dayjs(),
          }}
          disabled={!admission}
        >
          <Row gutter={[12, 8]} className={styles.packageNameRow}>
            <Col xs={24} sm={12} className={styles.packageNameCol}>
              <Form.Item
                label="Package Name"
                name="package"
                className={styles.assignPackagePackageField}
                rules={[{ required: true, message: "Please select a package" }]}
              >
                <Select
                  className={inputStyles.mdSelect}
                  placeholder="Select a Package"
                  popupMatchSelectWidth={isMobile}
                  placement="bottomLeft"
                  getPopupContainer={isMobile ? () => document.body : undefined}
                  dropdownStyle={
                    isMobile
                      ? { maxWidth: "calc(100vw - 24px)" }
                      : { maxWidth: "min(100vw - 32px, 400px)" }
                  }
                  onChange={(value) => {
                    setSelectPackageId(value);
                    const item = packages?.find((p) => p.id === value);
                    if (item != null) setTotalPrice(item.total_price);
                  }}
                >
                  {packages?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                      <Tag>{item.total_price}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {selectPackageId && (
                <>
                  {!viewDetails && (
                    <Typography.Link onClick={() => setViewDetails(true)}>
                      View More Details
                    </Typography.Link>
                  )}

                  {viewDetails && (
                    <PackageTable
                      selectPackageId={selectPackageId}
                      setSelectPackageId={setSelectPackageId}
                      setViewDetails={setViewDetails}
                      viewDetails={viewDetails}
                    />
                  )}
                  <br />
                </>
              )}
            </Col>
          </Row>
          <Row gutter={[12, 8]} align="top" className={styles.discountDateRow}>
            <Col xs={24} sm={12} className={styles.discountDateDiscountCol}>
              <Form.Item
                name="discount"
                label="Discount"
                className={styles.discountFieldGroup}
                rules={[
                  {
                    validator: (_, value, callback) => {
                      if (value && value !== "") {
                        if (/^[0-9]*\.?[0-9]{1,3}$/.test(value)) {
                          if (discountType === true && Number(value) > 100) {
                            callback("Discount cannot be greater than 100%");
                          } else if (
                            discountType === false &&
                            Number(value) > Number(totalPrice)
                          ) {
                            callback(
                              "Discount cannot be greater than total amount"
                            );
                          } else {
                            callback();
                          }
                        } else {
                          callback("Please enter a valid discount");
                        }
                      } else {
                        callback();
                      }
                    },
                  },
                ]}
              >
                <div className={styles.discountSplitRow}>
                  <Input
                    placeholder="Enter discount"
                    size={isMobile ? "large" : undefined}
                    className={inputStyles.mdInput}
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                  />
                  <Select
                    className={inputStyles.mdSelect}
                    size={isMobile ? "large" : undefined}
                    value={discountType}
                    onChange={onDiscountTypeChange}
                    popupMatchSelectWidth={false}
                  >
                    {priceTagList.map((data) => (
                      <Select.Option key={data.id} value={data.id}>
                        {data.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} className={styles.discountDateDateCol}>
              {isMobile ? (
                <>
                  <Form.Item
                    label="Date"
                    name="mobile_pkg_date"
                    rules={[
                      { required: true, message: "Please select a date" },
                    ]}
                  >
                    <DatePicker
                      allowClear={false}
                      className={inputStyles.mdDateInput}
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                      placement="bottomLeft"
                      getPopupContainer={() => document.body}
                      popupClassName="curengo-mobile-picker"
                      popupStyle={{
                        maxWidth: "min(100vw - 24px, 360px)",
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Time"
                    name="mobile_pkg_time"
                    rules={[
                      { required: true, message: "Please select a time" },
                    ]}
                  >
                    <TimePicker
                      allowClear={false}
                      className={inputStyles.mdDateInput}
                      format="hh:mm A"
                      use12Hours
                      needConfirm={false}
                      style={{ width: "100%" }}
                      getPopupContainer={() => document.body}
                      popupClassName="curengo-mobile-picker"
                      popupStyle={{
                        maxWidth: "min(100vw - 24px, 220px)",
                      }}
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item label="Date" name="start_date">
                  <DatePicker
                    showTime={{ format: "HH:mm A" }}
                    allowClear={false}
                    className={inputStyles.mdDateInput}
                    format="YYYY-MM-DD HH:mm A"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
          {selectPackageId && (
            <div style={{ marginTop: "10px" }}>
              {discount > 0 && (
                <>
                  <strong>Total Price After Discount:</strong> ₹
                  {discountType
                    ? (totalPrice - (discount / 100) * totalPrice).toFixed(2)
                    : (totalPrice - Number(discount)).toFixed(2)}
                </>
              )}
            </div>
          )}
          {!isMobile && (
            <Form.Item
              className={styles.assignPackageSubmit}
              style={{ textAlign: "right" }}
            >
              <Button
                type="primary"
                htmlType="submit"
                className={`${btnStyles.mdBtn} ${styles.assignPackageSubmitBtn}`}
              >
                Submit
              </Button>
            </Form.Item>
          )}
        </Form>
      </Card>

      <PatientPackageDetails refresh={refresh} patientData={patientData} />
    </>
  );
};

export default AssignPackages;
